import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Reveal, Eyebrow } from "./Motion";

const projects = [
  { id: "k1", cat: "Kitchens", title: "Waterfall Island Kitchen", img: "/img/bp-kitchen-island.jpg" },
  { id: "b1", cat: "Bathrooms", title: "Spa Walk-in Shower", img: "/img/bp-shower.jpg" },
  { id: "d1", cat: "Outdoor", title: "Pergola Deck", img: "/img/bp-deck.jpg" },
  { id: "k2", cat: "Kitchens", title: "Shaker Cabinet Wall", img: "/img/bp-shaker.jpg" },
  { id: "b2", cat: "Bathrooms", title: "Double Vanity Bath", img: "/img/bp-bath.jpg" },
  { id: "d2", cat: "Outdoor", title: "Florida Lanai Addition", img: "/img/bp-porch.jpg" },
];
const cats = ["All", "Kitchens", "Bathrooms", "Outdoor"];

export const Projects = () => {
  const [cat, setCat] = useState("All");
  const [active, setActive] = useState(null);
  const list = cat === "All" ? projects : projects.filter((p) => p.cat === cat);

  return (
    <section id="projects" className="py-24 lg:py-36" data-testid="projects-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between mb-12">
          <Reveal>
            <Eyebrow>From the drawing board</Eyebrow>
            <h2 className="mt-5 font-serif-d text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tight leading-[0.95]">
              Every project starts <span className="italic text-[#B04E2A]">on paper.</span>
            </h2>
            <p className="mt-4 max-w-md text-sm text-[#605A52]">The kinds of spaces we plan and build — measured, drawn and thought through before a single cut.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-wrap gap-2" role="tablist">
              {cats.map((c) => (
                <button key={c} onClick={() => setCat(c)} data-testid={`project-filter-${c.toLowerCase()}`}
                  className={`relative rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-300 ${cat === c ? "text-[#F8F6F0]" : "text-[#1E1B18] hover:text-[#B04E2A]"}`}>
                  {cat === c && <motion.span layoutId="filter-pill" className="absolute inset-0 rounded-full bg-[#1E1B18]" transition={{ type: "spring", stiffness: 380, damping: 32 }} />}
                  <span className="relative">{c}</span>
                </button>
              ))}
            </div>
          </Reveal>
        </div>
        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <AnimatePresence>
            {list.map((p, i) => (
              <motion.button
                layout key={p.id} onClick={() => setActive(p)} data-testid={`project-card-${p.id}`}
                initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className={`group relative text-left ${i % 3 === 1 ? "lg:translate-y-12" : ""}`}
              >
                <div className="relative aspect-[4/5] overflow-hidden rounded-[20px] bg-[#1F3A7A]">
                  <img src={p.img} alt={p.title} loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-[1.2s] ease-[cubic-bezier(.22,1,.36,1)] group-hover:scale-105" />
                  <span className="absolute right-4 top-4 grid h-11 w-11 place-items-center rounded-full bg-[#F8F6F0] text-[#1E1B18] scale-75 opacity-0 transition-[opacity,transform] duration-500 group-hover:opacity-100 group-hover:scale-100">
                    <Plus className="h-5 w-5" />
                  </span>
                </div>
                <div className="mt-4 flex items-baseline justify-between gap-4">
                  <h3 className="font-serif-d text-2xl font-semibold">{p.title}</h3>
                  <span className="font-mono-d text-xs uppercase tracking-[0.2em] text-[#605A52]">{p.cat}</span>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-4xl border-[#E2DDD5] bg-[#F8F6F0] p-3 sm:p-4" data-testid="project-modal">
          {active && (
            <>
              <img src={active.img} alt={active.title} className="max-h-[75vh] w-full rounded-xl object-contain bg-[#1F3A7A]" />
              <div className="px-2 pb-2">
                <DialogTitle className="font-serif-d text-3xl font-semibold">{active.title}</DialogTitle>
                <DialogDescription className="font-mono-d text-xs uppercase tracking-[0.2em] text-[#605A52] mt-1">{active.cat}</DialogDescription>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
